import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Ic, icons } from "../../components/Icons";
import Spinner from "../../components/Spinner";
import useDashboardData from "../hooks/useDashboardData";
import OnboardingOverlay from "./components/OnboardingOverlay";

function resolveAccessTone(betaAccess) {
  if (!betaAccess || betaAccess.gatingEnabled === false) {
    return {
      dot: "#34d399",
      border: "rgba(16,185,129,.22)",
      background: "rgba(16,185,129,.08)",
    };
  }

  if (betaAccess.allowed) {
    return {
      dot: "#38bdf8",
      border: "rgba(56,189,248,.22)",
      background: "rgba(56,189,248,.08)",
    };
  }

  if (["rejected", "removed", "expired"].includes(betaAccess.accessStatus)) {
    return {
      dot: "#f87171",
      border: "rgba(248,113,113,.22)",
      background: "rgba(248,113,113,.08)",
    };
  }

  return {
    dot: "#fbbf24",
    border: "rgba(251,191,36,.22)",
    background: "rgba(251,191,36,.08)",
  };
}

function resolveAccessTitle(betaAccess) {
  if (!betaAccess || betaAccess.gatingEnabled === false) return "API access is currently open";
  if (betaAccess.allowed) return "API beta access approved";
  if (betaAccess.decision === "product_mismatch") return "Your beta record does not include API access yet";
  if (betaAccess.decision === "not_registered") return "Developer API access requires beta approval";
  if (betaAccess.accessStatus === "rejected") return "This beta request was not approved";
  if (betaAccess.accessStatus === "removed") return "Developer beta access was removed";
  if (betaAccess.accessStatus === "expired") return "Your beta invite expired";
  return "Developer API access is still pending";
}

function resolveApiKeyStatus(betaAccess, devData) {
  if (!betaAccess || betaAccess.gatingEnabled === false) {
    return devData?.hasApiKey
      ? { label: "Active", dot: "#34d399", detail: devData.apiKey ? `${devData.apiKey.slice(0, 12)}...` : devData?.apiKeyPrefix || "Key ready" }
      : { label: "Provisioning", dot: "#fbbf24", detail: "Your key will appear once the profile finishes provisioning." };
  }

  if (!betaAccess.allowed) {
    return {
      label: "Locked",
      dot: "#fbbf24",
      detail: betaAccess.message,
    };
  }

  if (devData?.hasApiKey || devData?.apiKey) {
    return {
      label: betaAccess.accessStatus === "active" ? "Active" : "Approved",
      dot: "#34d399",
      detail: devData.apiKey ? `${devData.apiKey.slice(0, 12)}...` : devData?.apiKeyPrefix || "Key ready",
    };
  }

  return {
    label: "Approved",
    dot: "#38bdf8",
    detail: "Your access is approved. Generate or refresh a key to start making requests.",
  };
}

export default function DashboardPageView() {
  const navigate = useNavigate();
  const [keyVisible, setKeyVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState("overview");
  const {
    user,
    devData,
    loading,
    onboardingDone,
    completeOnboarding,
    rotateApiKey,
  } = useDashboardData(navigate);

  const betaAccess = devData?.betaAccess || null;
  const canUseApi = !betaAccess || betaAccess.allowed || betaAccess.gatingEnabled === false;
  const showOnboardingOverlay = canUseApi && devData?.onboardingComplete === false && !onboardingDone;
  const tone = resolveAccessTone(betaAccess);
  const keyStatus = resolveApiKeyStatus(betaAccess, devData);

  const handleCopy = () => {
    if (devData?.apiKey) {
      navigator.clipboard.writeText(devData.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRegenerate = async () => {
    if (!user || !canUseApi) return;
    if (!window.confirm("Are you sure? Your current key will stop working immediately.")) return;
    try {
      await rotateApiKey();
    } catch (e) {
      alert("Error regenerating key: " + e.message);
    }
  };

  if (loading) {
    return (
      <div style={{ background: "var(--bg-primary)", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, paddingTop: 52 }}>
        <Spinner />
        <span style={{ fontSize: 13, color: "var(--text-muted)", letterSpacing: "-.01em" }}>Loading…</span>
      </div>
    );
  }

  const planLabel = { starter: "Starter", pro: "Pro", enterprise: "Enterprise" };
  const planLimit = { starter: "100 req/min", pro: "500 req/min", enterprise: "Custom" };

  const cardStyle = {
    background: "var(--bg-card)",
    borderRadius: 20,
    border: "1px solid var(--border-subtle)",
    padding: "28px 28px",
  };

  return (
    <div style={{ background: "var(--bg-primary)", paddingTop: 52, minHeight: "100vh" }}>
      {showOnboardingOverlay && (
        <OnboardingOverlay devData={devData} onComplete={completeOnboarding} />
      )}

      <div style={{ background: "var(--bg-elevated)", borderBottom: "1px solid var(--border-subtle)", padding: "16px 0" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 2rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.02em" }}>Dashboard</h1>
            <span style={{
              fontSize: 11, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase",
              color: "#a78bfa", background: "rgba(99,102,241,.15)", border: "1px solid rgba(99,102,241,.25)",
              borderRadius: 999, padding: "3px 10px",
            }}>
              {planLabel[devData?.plan] || "Starter"}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <a href="/ax" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "var(--text-tertiary)", textDecoration: "none", transition: "color .2s" }}>Docs ↗</a>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "32px 2rem 80px" }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
          background: tone.background, border: `1px solid ${tone.border}`,
          borderRadius: 14, padding: "18px 22px", marginBottom: 28,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: tone.dot, flexShrink: 0, boxShadow: `0 0 8px ${tone.dot}66` }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>{resolveAccessTitle(betaAccess)}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{betaAccess?.message || "Your developer account is ready."}</div>
            </div>
          </div>
          {!canUseApi ? (
            <Link to="/#contact" className="btn btn-ghost btn-sm" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>Contact team →</Link>
          ) : (
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {betaAccess?.gatingEnabled === false ? "Runtime beta gate is currently off." : `Access status: ${betaAccess?.accessStatus || "active"}`}
            </span>
          )}
        </div>

        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 15, color: "var(--text-tertiary)" }}>
            Welcome back, <Link to="/profile" style={{ color: "var(--text-primary)", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid transparent", transition: "border-color .15s" }} onMouseEnter={(e) => e.currentTarget.style.borderBottomColor = "var(--text-primary)"} onMouseLeave={(e) => e.currentTarget.style.borderBottomColor = "transparent"}>{user?.displayName || devData?.name || "Developer"}</Link>
          </div>
        </div>

        <div style={{ marginBottom: 28 }}>
          <div className="seg">
            {["overview", "api-key", "usage"].map((value) => (
              <button key={value} className={`seg-btn${tab === value ? " active" : ""}`} onClick={() => setTab(value)}>
                {value === "overview" ? "Overview" : value === "api-key" ? "API Key" : "Usage"}
              </button>
            ))}
          </div>
        </div>

        {tab === "overview" && (
          <div className="grid-3">
            <div style={cardStyle}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 12 }}>API Key Status</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: keyStatus.dot }} />
                <span style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>{keyStatus.label}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--text-dim)", lineHeight: 1.6 }}>
                {keyStatus.detail}
              </div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 12 }}>Current Plan</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.03em", marginBottom: 4 }}>
                {planLabel[devData?.plan] || "Starter"}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-dim)" }}>
                {planLimit[devData?.plan] || "100 req/min"}
              </div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 12 }}>Designs Generated</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.03em", marginBottom: 4 }}>
                {canUseApi ? devData?.usage?.designs ?? 0 : "—"}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-dim)" }}>
                {canUseApi ? `${devData?.usage?.requests ?? 0} total API requests` : "Usage unlocks after approval."}
              </div>
            </div>

            <div style={{ ...cardStyle, gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 16 }}>Quick links</div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Link to="/ax" className="btn btn-ghost btn-sm">Ateno AX</Link>
                {!canUseApi && <Link to="/#contact" className="btn btn-ghost btn-sm">Contact team</Link>}
                <a href="mailto:info@ateno.co" className="btn btn-ghost btn-sm">Contact Support</a>
              </div>
            </div>
          </div>
        )}

        {tab === "api-key" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={cardStyle}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 }}>Your API Key</div>
              {!canUseApi ? (
                <>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 18 }}>
                    {betaAccess?.message || "API access is currently invite-only. Once you're approved, your key will appear here."}
                  </p>
                  <Link to="/#contact" className="btn btn-outline btn-sm">Contact team →</Link>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 20 }}>
                    Use this key in the <code style={{ fontFamily: "SF Mono,Menlo,monospace", fontSize: 12, color: "#a78bfa" }}>Authorization</code> header of every API request. Keep it secret and never commit it to version control.
                  </p>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{
                      flex: 1, minWidth: 200, background: "var(--bg-elevated)", border: "1px solid var(--border-color)", borderRadius: 12,
                      padding: "12px 16px", fontFamily: "SF Mono,Menlo,monospace", fontSize: 13,
                      color: keyVisible ? "var(--text-bright)" : "var(--text-dim)",
                      letterSpacing: keyVisible ? 0 : ".15em",
                      userSelect: keyVisible ? "all" : "none", wordBreak: "break-all",
                    }}>
                      {devData?.apiKey ? (keyVisible ? devData.apiKey : "•".repeat(36)) : (devData?.apiKeyPrefix || "Key will appear here once available")}
                    </div>
                    <button onClick={() => setKeyVisible(!keyVisible)} className="btn btn-ghost btn-sm" style={{ minWidth: 80 }} disabled={!devData?.apiKey}>
                      <Ic d={icons.eye} size={14} color="var(--text-secondary)" />
                      {keyVisible ? "Hide" : "Reveal"}
                    </button>
                    <button onClick={handleCopy} className="btn btn-ghost btn-sm" style={{ minWidth: 80 }} disabled={!devData?.apiKey}>
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </>
              )}
            </div>

            <div style={cardStyle}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 }}>Regenerate Key</div>
              <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 16 }}>
                If your key is compromised, regenerate it. The old key will stop working immediately.
              </p>
              <button onClick={handleRegenerate} className="btn btn-sm" disabled={!canUseApi} style={{ background: canUseApi ? "rgba(248,113,113,.12)" : "rgba(148,163,184,.12)", border: `1px solid ${canUseApi ? "rgba(248,113,113,.25)" : "rgba(148,163,184,.18)"}`, color: canUseApi ? "#f87171" : "var(--text-dim)" }}>
                Regenerate API key
              </button>
            </div>
          </div>
        )}

        {tab === "usage" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {canUseApi ? (
              <>
                <div className="grid-3">
                  <div style={cardStyle}>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 12 }}>Total Requests</div>
                    <div style={{ fontSize: 32, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.04em" }}>
                      {devData?.usage?.requests ?? 0}
                    </div>
                  </div>
                  <div style={cardStyle}>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 12 }}>Designs Created</div>
                    <div style={{ fontSize: 32, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.04em" }}>
                      {devData?.usage?.designs ?? 0}
                    </div>
                  </div>
                  <div style={cardStyle}>
                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 12 }}>Rate Limit</div>
                    <div style={{ fontSize: 32, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.04em" }}>
                      {planLimit[devData?.plan] || "100/m"}
                    </div>
                  </div>
                </div>

                <div style={{ ...cardStyle, textAlign: "center", padding: "48px 28px" }}>
                  <div style={{ marginBottom: 12 }}>
                    <Ic d={icons.zap} size={28} color="rgba(167,139,250,.4)" />
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 }}>Usage analytics coming soon</div>
                  <p style={{ fontSize: 13, color: "var(--text-dim)", maxWidth: 360, margin: "0 auto" }}>
                    Detailed request logs, latency graphs, and error tracking will appear here as you start using the API.
                  </p>
                </div>
              </>
            ) : (
              <div style={{ ...cardStyle, textAlign: "center", padding: "48px 28px" }}>
                <div style={{ marginBottom: 12 }}>
                  <Ic d={icons.lock} size={28} color="rgba(251,191,36,.55)" />
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 }}>Usage unlocks after beta approval</div>
                <p style={{ fontSize: 13, color: "var(--text-dim)", maxWidth: 420, margin: "0 auto 18px" }}>
                  {betaAccess?.message || "Once your developer access is approved, request analytics and API usage will appear here automatically."}
                </p>
                <Link to="/#contact" className="btn btn-outline btn-sm">Contact team →</Link>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}
